import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getIntegrations,
  updateIntegrationStatus,
  updateCustomerIntegrationStatus,
  updateIntegrationConfig,
  validateIntegrationConfig,
  deleteCustomerIntegration,
  getSubscriptionStatus,
} from '../api';
import IntegrationsList from '../components/settings/IntegrationsList';
import GenericIntegrationConfig from '../components/settings/integrations/GenericIntegrationConfig';
import UserManagement from '../components/settings/UserManagement';
import ActiveIntegrations from '../components/settings/ActiveIntegrations';
import ActivePlanDetails from '../components/settings/ActivePlanDetails';
import MarketDataSettings from '../components/settings/MarketDataSettings';
import AccountsBilling from '../components/settings/AccountsBilling';
import { IntegrationWrapper, CustomerIntegration } from '../components/settings/types';
import LoadingSpinner from '../components/LoadingSpinner';
import { SubscriptionStatus } from '../types/PlanTypes';

const Settings: React.FC = () => {
  const location = useLocation();
  const { user: currentUser } = useAuth();

  // Integration state
  const [integrations, setIntegrations] = useState<IntegrationWrapper[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [configuring, setConfiguring] = useState<number | null>(null);
  const [configValues, setConfigValues] = useState<Record<string, any>>({});
  const [configErrors, setConfigErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationSuccess, setValidationSuccess] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [errorModal, setErrorModal] = useState<{ overrideTitle?: string, show: boolean, message: string, navigationLabel?: string, onNavigate?: () => void }>({ show: false, message: '' });
  const [customerIntegrationId, setCustomerIntegrationId] = useState<number | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionStatus | null>(null);
  const [currentPlanObject, setCurrentPlanObject] = useState<any>(null);

  // Active integration index for multiple integrations
  const [activeIntegrationIndex, setActiveIntegrationIndex] = useState<number>(0);

  // Navigation state - initialize from URL parameter if present
  const [activeTab, setActiveTab] = useState(() => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    return tabParam || 'integrations';
  });

  // Responsive: track mobile layout
  const [isMobile, setIsMobile] = useState<boolean>(false);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  // Plan data state
  const [planData, setPlanData] = useState<any>(null);

  const updateIntegrationStatuses = async (customerIntegrations: CustomerIntegration[]) => {
    // Update the status information for each customer integration
    setIntegrations(prevIntegrations =>
      prevIntegrations.map(integration => ({
        ...integration,
        customerIntegrations: integration.customerIntegrations?.map(ci => {
          // Find the updated customer integration data
          const updatedCI = customerIntegrations.find(updated => updated.id === ci.id);
          if (updatedCI) {
            return {
              ...ci,
              lastRunTime: updatedCI.lastRunTime,
              nextScheduledRun: updatedCI.nextScheduledRun,
              status: updatedCI.status,
              statusReason: updatedCI.statusReason
            };
          }
          return ci;
        })
      }))
    );
  }

  const fetchIntegrations = async () => {
    try {
      //const integrationDataResponse = await getIntegrations();

      const [integrationDataResponse, subscriptionResponse] = await Promise.all([
        getIntegrations(),
        getSubscriptionStatus().catch(() => null) // Don't fail if no subscription
      ]);

      console.log('data', integrationDataResponse);
      setIntegrations(integrationDataResponse.available);
      setPlanData(integrationDataResponse.plan);

      let subData: SubscriptionStatus | null = null;

      // Set subscription info
      if (subscriptionResponse) {
        subData = subscriptionResponse;
        setSubscriptionInfo(subData);
      }

      const currentPlanObject = integrationDataResponse.plan && subscriptionResponse ? {
        ...integrationDataResponse.plan,
        SubscriptionStatus: subscriptionResponse?.status,
        PlanEndDate: subscriptionResponse?.planEndDate,
        PendingPlanType: subscriptionResponse?.pendingPlanType,
        PendingPlanEffectiveDate: subscriptionResponse?.planChangeEffectiveDate,
        DiscountCouponName: subscriptionResponse?.discountCouponName,
        DiscountEndDate: subscriptionResponse?.discountEndDate,
        DiscountStripeId: subscriptionResponse?.discountStripeId
      } : null

      setCurrentPlanObject(currentPlanObject);


      setLoading(false);
      return integrationDataResponse.available;
    } catch (err) {
      console.error('Error fetching integrations:', err);
      setError('Failed to load integrations');
      setLoading(false);
      throw err;
    }
  };

  useEffect(() => {
    fetchIntegrations();
  }, []);



  const handleStatusToggle = async (id: number, currentStatus: boolean) => {
    try {
      const newStatus = !currentStatus;

      if (newStatus) {
        const currentlyEnabledCount = integrations.reduce((count, integration) => {
          const enabledCustomerIntegrations = integration.customerIntegrations?.filter(ci => ci.isEnabled) || [];
          return count + enabledCustomerIntegrations.length;
        }, 0);


        if (!planData) {
          setErrorModal({
            show: true,
            overrideTitle: 'Plan Required',
            message: `You are not currently on a plan. Please upgrade to the next plan level to enable integrations.`,
            navigationLabel: 'See Available Plans',
            onNavigate: () => setActiveTab('accounts-billing')
          });
          return;
        }

        if (currentlyEnabledCount >= planData.TotalIntegrations) {
          setErrorModal({
            show: true,
            overrideTitle: 'Plan Required',
            message: `You have reached the maximum number of integrations (${planData.TotalIntegrations}) allowed on your current plan. Please upgrade to the next plan level to enable additional integrations.`,
            navigationLabel: 'See Available Plans',
            onNavigate: () => setActiveTab('accounts-billing')
          });
          return;
        }
      }


      await updateIntegrationStatus(id, newStatus);

      // Update local state
      setIntegrations(prevIntegrations =>
        prevIntegrations.map(integration =>
          integration.integration.Id === id ? { ...integration, enabled: newStatus } : integration
        )
      );

      // Refresh the active integrations list
      await fetchIntegrations();
    } catch (err) {
      console.error('Error toggling integration status:', err);
      setErrorModal({
        show: true,
        message: `Failed to update integration status: ${err instanceof Error ? err.message : 'Unknown error'}`
      });
    }
  };

  const handleCustomerIntegrationToggle = async (customerIntegrationId: number, currentStatus: boolean) => {
    try {
      const newStatus = !currentStatus;

      // If trying to enable a customer integration, check plan limits
      if (newStatus && (!planData || planData?.TotalIntegrations)) {
        // Count currently enabled customer integrations
        const currentlyEnabledCount = integrations.reduce((count, integration) => {
          const enabledCustomerIntegrations = integration.customerIntegrations?.filter(ci => ci.isEnabled) || [];
          return count + enabledCustomerIntegrations.length;
        }, 0);

        if (!planData) {
          setErrorModal({
            show: true,
            overrideTitle: 'Plan Required',
            message: `You are not currently on a plan. Please upgrade to the next plan level to enable integrations.`,
            navigationLabel: 'See Available Plans',
            onNavigate: () => setActiveTab('accounts-billing')
          });
          return;
        }

        if (currentlyEnabledCount >= planData.TotalIntegrations) {
          setErrorModal({
            show: true,
            overrideTitle: 'Plan Required',
            message: `You have reached the maximum number of integrations (${planData.TotalIntegrations}) allowed on your current plan. Please upgrade to the next plan level to enable additional integrations.`,
            navigationLabel: 'See Available Plans',
            onNavigate: () => setActiveTab('accounts-billing')
          });
          return;
        }
      }

      await updateCustomerIntegrationStatus(customerIntegrationId, newStatus);

      // Update local state
      setIntegrations(prevIntegrations =>
        prevIntegrations.map(integration => ({
          ...integration,
          customerIntegrations: integration.customerIntegrations?.map(ci =>
            ci.id === customerIntegrationId ? { ...ci, isEnabled: newStatus } : ci
          )
        }))
      );

      // Refresh the integrations list to get updated data
      await fetchIntegrations();
    } catch (err) {
      console.error('Error toggling customer integration status:', err);
      setErrorModal({
        show: true,
        message: `Failed to update customer integration status: ${err instanceof Error ? err.message : 'Unknown error'}`
      });
    }
  };

  const handleConfigure = async (integration: IntegrationWrapper) => {
    setConfiguring(integration.integration.Id);
    setConfigErrors({});
    setSaveSuccess(false);
    setValidationSuccess(false);
    setActiveIntegrationIndex(0);

    // First, refresh the integrations list to get the latest data
    try {
      const refreshedData = await getIntegrations();
      setIntegrations(refreshedData.available);

      // Find the updated integration with the latest data
      const refreshedIntegration = refreshedData.available.find((i: IntegrationWrapper) => i.integration.Id === integration.integration.Id);
      if (refreshedIntegration) {
        integration = refreshedIntegration;
      }
    } catch (err) {
      console.error('Failed to refresh integrations list:', err);
    }

    // Reset active integration index
    setActiveIntegrationIndex(0);

    // Check if this integration allows multiple configurations
    const allowMultiple = integration.integration.AllowMultiple || false;

    // Handle case where integration has customer integrations
    if (integration.customerIntegrations && integration.customerIntegrations.length > 0) {
      try {
        console.log(`Loading configurations for integration ID ${integration.integration.Id}`);

        // Use the first integration's config values for the main form
        const firstIntegration = integration.customerIntegrations[0];
        setConfigValues(firstIntegration.configValues);

        // Customer integrations are already in the integration object, no need to set separately

        // We'll let the user validate the configuration manually by clicking the validate button
        // This prevents automatic validation when opening the config dialog
      } catch (err) {
        console.error(`Failed to load configurations for ${integration.integration.Name}:`, err);
        setConfigValues({});
        setErrorModal({
          show: true,
          message: `Failed to load configurations for ${integration.integration.Name}`
        });
      }
    } else {
      // Initialize empty config for new integrations
      const initialConfig = integration.configFields.reduce((acc, field) => {
        acc[field.name] = '';
        return acc;
      }, {} as Record<string, string>);

      setConfigValues(initialConfig);

      // If this integration allows multiple configurations but doesn't have any yet,
      // initialize with an empty one
      if (allowMultiple) {
        // Initialize the customerIntegrations array directly in the integration object
        setIntegrations(prevIntegrations => {
          const updatedIntegrations = [...prevIntegrations];
          const integrationIndex = updatedIntegrations.findIndex(i => i.integration.Id === configuring);

          if (integrationIndex >= 0) {
            updatedIntegrations[integrationIndex] = {
              ...updatedIntegrations[integrationIndex],
              customerIntegrations: [{
                id: 0, // Use 0 to indicate a new integration
                configValues: initialConfig,
                status: null,
                statusReason: null,
                lastRunTime: null,
                nextScheduledRun: null,
                isEnabled: false
              }]
            };
          }

          return updatedIntegrations;
        });
      }
    }

    // Store customer integration ID if available and load metadata
    if (integration.customerIntegrations && integration.customerIntegrations.length > 0) {
      const firstIntegration = integration.customerIntegrations[0];
      setCustomerIntegrationId(firstIntegration.id);
    } else {
      setCustomerIntegrationId(null);
    }
  };

  const handleConfigChange = (name: string, value: string) => {
    setConfigValues(prev => ({
      ...prev,
      [name]: value
    }));

    // If we're using multiple integrations, update the current integration's config values
    if (configuring) {
      const integration = integrations.find(i => i.integration.Id === configuring);
      if (integration?.customerIntegrations?.[activeIntegrationIndex]) {
        setIntegrations(prevIntegrations => {
          const updatedIntegrations = [...prevIntegrations];
          const integrationIndex = updatedIntegrations.findIndex(i => i.integration.Id === configuring);

          if (integrationIndex >= 0) {
            const customerIntegrations = updatedIntegrations[integrationIndex]?.customerIntegrations;

            if (customerIntegrations && activeIntegrationIndex < customerIntegrations.length) {
              const customerIntegration = customerIntegrations[activeIntegrationIndex];
              if (customerIntegration) {
                customerIntegration.configValues = {
                  ...customerIntegration.configValues,
                  [name]: value
                };
              }
            }
          }

          return updatedIntegrations;
        });
      }
    }

    // Clear any validation errors for this field
    if (configErrors[name]) {
      setConfigErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleActiveIntegrationChange = (index: number) => {
    // Don't do anything if the index is out of bounds
    if (configuring) {
      const integration = integrations.find(i => i.integration.Id === configuring);
      const customerIntegrations = integration?.customerIntegrations;

      if (!customerIntegrations || index < 0 || index >= customerIntegrations.length) return;

      // Save the current config values to the current integration
      setIntegrations(prevIntegrations => {
        const updatedIntegrations = [...prevIntegrations];
        const integrationIndex = updatedIntegrations.findIndex(i => i.integration.Id === configuring);

        if (integrationIndex >= 0) {
          const customerIntegrations = updatedIntegrations[integrationIndex]?.customerIntegrations;

          if (customerIntegrations && activeIntegrationIndex < customerIntegrations.length) {
            const customerIntegration = customerIntegrations[activeIntegrationIndex];
            if (customerIntegration) {
              customerIntegration.configValues = configValues;
            }
          }
        }

        return updatedIntegrations;
      });

      // Update the active index
      setActiveIntegrationIndex(index);

      // Load the config values from the new active integration
      if (customerIntegrations[index]) {
        setConfigValues(customerIntegrations[index].configValues);

        // If this is an existing integration, set the customer integration ID
        if (customerIntegrations[index].id > 0) {
          setCustomerIntegrationId(customerIntegrations[index].id);
        } else {
          setCustomerIntegrationId(null);
        }

        // Reset validation state
        setValidationSuccess(false);
        setConfigErrors({});
      }
    }
  };

  const handleAddIntegration = () => {
    if (!configuring) return;

    // Add a new integration to the list
    setIntegrations(prevIntegrations => {
      const updatedIntegrations = [...prevIntegrations];
      const integrationIndex = updatedIntegrations.findIndex(i => i.integration.Id === configuring);

      if (integrationIndex >= 0) {
        // Create a new integration with default/empty values
        const integration = updatedIntegrations[integrationIndex];

        // Create empty config values based on the integration's config fields
        const emptyConfigValues = integration.configFields.reduce((acc, field) => {
          acc[field.name] = '';
          return acc;
        }, {} as Record<string, string>);

        const newIntegration = {
          id: 0, // Use 0 to indicate a new integration
          configValues: emptyConfigValues,
          status: null,
          statusReason: null,
          lastRunTime: null,
          nextScheduledRun: null,
          isEnabled: false
        };

        // Add the new integration to the customerIntegrations array
        const updatedCustomerIntegrations = [
          ...(updatedIntegrations[integrationIndex].customerIntegrations || []),
          newIntegration
        ];

        updatedIntegrations[integrationIndex] = {
          ...updatedIntegrations[integrationIndex],
          customerIntegrations: updatedCustomerIntegrations
        };

        // Set the active index to the new integration
        const newIndex = updatedCustomerIntegrations.length - 1;
        setActiveIntegrationIndex(newIndex);

        // Update the configValues state to show the empty values of the new integration
        setConfigValues(emptyConfigValues);

        // Reset validation state for the new configuration
        setValidationSuccess(false);
        setConfigErrors({});
      }

      return updatedIntegrations;
    });
  };
  const validateConfig = (integration: IntegrationWrapper, valuesOverride?: Record<string, any>) => {
    const errors: Record<string, string> = {};
    const valuesToValidate = valuesOverride || configValues;

    integration.configFields.forEach(field => {
      if (field.required && !valuesToValidate[field.name]) {
        errors[field.name] = `${field.label} is required`;
      }

      if (field.type === 'email' && valuesToValidate[field.name] &&
        !/^\S+@\S+\.\S+$/.test(valuesToValidate[field.name])) {
        errors[field.name] = 'Please enter a valid email address';
      }
    });

    return errors;
  };

  const handleValidateConfig = async () => {
    if (!configuring) return;

    const integration = integrations.find(i => i.integration.Id === configuring);
    if (!integration) return;

    // First validate required fields
    const errors = validateConfig(integration);
    if (Object.keys(errors).length > 0) {
      setConfigErrors(errors);
      return;
    }

    setValidating(true);
    setValidationSuccess(false);

    try {
      // Use the current config values for validation
      const currentConfigValues = configValues;

      const result = await validateIntegrationConfig(integration.integration.Id, currentConfigValues);

      if (result.valid) {
        setValidationSuccess(true);

        // Update the validation state for the current customer integration
        setIntegrations(prevIntegrations => {
          const updatedIntegrations = [...prevIntegrations];
          const integrationIndex = updatedIntegrations.findIndex(i => i.integration.Id === configuring);

          if (integrationIndex >= 0) {
            const integration = updatedIntegrations[integrationIndex];

            // Initialize customerIntegrations array if it doesn't exist
            if (!integration.customerIntegrations) {
              integration.customerIntegrations = [];
            }

            // If we're in single integration mode and there's no customerIntegration, create one
            if (integration.customerIntegrations.length === 0) {
              // Create a new customer integration with default values
              const newCustomerIntegration: CustomerIntegration = {
                id: 0, // Use 0 to indicate a new integration
                configValues: currentConfigValues,
                status: null,
                statusReason: null,
                lastRunTime: null,
                nextScheduledRun: null,
                isEnabled: false
              };

              // Add the new customer integration
              integration.customerIntegrations.push(newCustomerIntegration);
              console.log('Created new customerIntegration in handleValidateConfig', newCustomerIntegration);
            }

            // Now update the configValues in the customerIntegration
            if (activeIntegrationIndex < integration.customerIntegrations.length) {
              const customerIntegration = integration.customerIntegrations[activeIntegrationIndex];
              if (customerIntegration) {
                customerIntegration.configValues = currentConfigValues;
                console.log('Updated configValues in customerIntegration', customerIntegration);
              }
            }
          }

          return updatedIntegrations;
        });
      } else {
        setConfigErrors(result.errors);
      }
    } catch (err) {
      setErrorModal({
        show: true,
        message: `Validation failed: ${err instanceof Error ? err.message : 'Unknown error'}`
      });
    } finally {
      setValidating(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!configuring) return;

    // Find the current integration being configured
    const integration = integrations.find(i => i.integration.Id === configuring);
    if (!integration) return;

    // Check if this integration allows multiple configurations
    const allowMultiple = integration.integration.AllowMultiple || false;

    // First, validate the current configuration
    const errors = validateConfig(integration, configValues);
    if (Object.keys(errors).length > 0) {
      setConfigErrors(errors);
      return;
    }

    // Require validation before saving
    if (!validationSuccess) {
      setErrorModal({
        show: true,
        message: 'Please validate your configuration before saving'
      });
      return;
    }

    setSaving(true);
    setSaveSuccess(false);

    try {
      // If this integration allows multiple configurations, save only the active one
      if (allowMultiple && integration.customerIntegrations &&
        activeIntegrationIndex < integration.customerIntegrations.length) {
        // Get the current customer integration
        const customerIntegration = integration.customerIntegrations[activeIntegrationIndex];
        const currentConfig = configValues; // Use the current config values
        const currentId = customerIntegration?.id > 0 ? customerIntegration.id : null;

        const defaultHour = 1; // Default hour if not specified       

        // Get the most up-to-date integration data from state
        const currentIntegration = integrations.find(i => i.integration.Id === configuring);
        const currentCustomerIntegration = currentIntegration?.customerIntegrations?.[activeIntegrationIndex];

        // Save the configuration        

        const result = await updateIntegrationConfig(integration.integration.Id, configValues, currentId);

        // Only update the ID if this was a new integration
        if (result && result.customerIntegrationId && !currentId) {
          setIntegrations(prevIntegrations => {
            const updatedIntegrations = [...prevIntegrations];
            const integrationIndex = updatedIntegrations.findIndex(i => i.integration.Id === configuring);

            if (integrationIndex >= 0) {
              const customerIntegrations = updatedIntegrations[integrationIndex]?.customerIntegrations;

              if (customerIntegrations && activeIntegrationIndex < customerIntegrations.length) {
                // Only update the ID, since the rest of the data is already in state
                customerIntegrations[activeIntegrationIndex] = {
                  ...customerIntegrations[activeIntegrationIndex],
                  id: result.customerIntegrationId
                };
              }
            }

            return updatedIntegrations;
          });
        }

        // Update local state
        setIntegrations(integrations.map(i =>
          i.integration.Id === integration.integration.Id ? { ...i, configured: true } : i
        ));

        setSaveSuccess(true);

        // Don't close the modal after saving a single integration
        // This allows the user to continue configuring other integrations
      } else {
        // Handle single integration case


        // For single integration case, we need to ensure we have a customerIntegration
        // If none exists, we need to create one with default values
        let customerIntegration: CustomerIntegration;

        if (integration.customerIntegrations && integration.customerIntegrations.length > 0) {
          // Use existing customer integration if available
          customerIntegration = integration.customerIntegrations[0];
        } else {
          // Create a new customer integration with default values
          customerIntegration = {
            id: 0, // Use 0 to indicate a new integration
            configValues: configValues,
            status: null,
            statusReason: null,
            lastRunTime: null,
            nextScheduledRun: null,
            isEnabled: false
          };

          // Add the new customer integration to the integration object
          setIntegrations(prevIntegrations => {
            const updatedIntegrations = [...prevIntegrations];
            const integrationIndex = updatedIntegrations.findIndex(i => i.integration.Id === configuring);

            if (integrationIndex >= 0) {
              updatedIntegrations[integrationIndex] = {
                ...updatedIntegrations[integrationIndex],
                customerIntegrations: [customerIntegration]
              };
            }

            return updatedIntegrations;
          });
        }

        // Ensure we have all required schedule fields with proper values
        // const schedule = {
        //   updateCadence: customerIntegration.updateCadence || 'daily',
        //   updateHour: customerIntegration.updateHour !== null && customerIntegration.updateHour !== undefined ? 
        //     customerIntegration.updateHour : defaultHour,
        //   updateDayOfWeek: customerIntegration.updateCadence === 'weekly' ? 
        //     (customerIntegration.updateDayOfWeek || 1) : null,
        //   updateDayOfMonth: customerIntegration.updateCadence === 'monthly' ? 
        //     (customerIntegration.updateDayOfMonth || 1) : null,
        //   monthsOfData: customerIntegration.monthsOfData || 1
        // };

        //console.log('Saving single integration with schedule:', schedule);

        // Save the configuration
        const result = await updateIntegrationConfig(integration.integration.Id, configValues, customerIntegrationId);

        if (result && result.customerIntegrationId) {
          setCustomerIntegrationId(result.customerIntegrationId);

          // Update the integration in the list
          setIntegrations(integrations.map(i =>
            i.integration.Id === integration.integration.Id ? { ...i, configured: true } : i
          ));

          setSaveSuccess(true);

          // Close modal after a short delay
          // setTimeout(() => {
          //   setConfiguring(null);
          // }, 1500);
        }
      }
    } catch (err) {
      setErrorModal({
        show: true,
        message: `Failed to save configuration: ${err instanceof Error ? err.message : 'Unknown error'}`
      });
    } finally {
      setSaving(false);
    }
  };

  const setCloseConfiguring = () => {
    setConfiguring(null);

    //remove all customer integrations with the id of 0 from the list because these have not been saved
    setIntegrations(integrations.map(i =>
      i.integration.Id === configuring ? { ...i, customerIntegrations: i.customerIntegrations?.filter(ci => ci.id !== 0) } : i
    ));
  };

  const isAdmin = currentUser?.role === 'Admin';

  if (loading) return <LoadingSpinner title="Loading settings..." />;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', paddingBottom: '2rem' }}>
      <h2 style={{ marginTop: isMobile ? 8 : undefined }}>Settings</h2>

      {error && (
        <div style={{
          padding: 16,
          backgroundColor: '#ffebee',
          color: '#c62828',
          borderRadius: 4,
          marginBottom: 24
        }}>
          {error}
        </div>
      )}

      {/* Success message */}
      {successMessage && (
        <div style={{
          padding: 16,
          backgroundColor: '#e8f5e9',
          color: '#2e7d32',
          borderRadius: 4,
          marginBottom: 24
        }}>
          {successMessage}
        </div>
      )}

      {/* Error message */}
      {errorMessage && (
        <div style={{
          padding: 16,
          backgroundColor: '#ffebee',
          color: '#c62828',
          borderRadius: 4,
          marginBottom: 24
        }}>
          {errorMessage}
        </div>
      )}

      {/* Error Modal */}
      {errorModal.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: 24,
            borderRadius: 8,
            maxWidth: 500,
            width: '100%',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            position: 'relative'
          }}>
            <button
              onClick={() => setErrorModal({ show: false, message: '' })}
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 24,
                color: '#666',
                padding: 0,
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
            <h3 style={{ marginTop: 0, color: '#d32f2f', paddingRight: 40 }}>{errorModal.overrideTitle || 'Error'}</h3>
            <p>{errorModal.message}</p>
            {errorModal.navigationLabel && errorModal.onNavigate && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 24 }}>
                <button
                  onClick={() => {
                    errorModal.onNavigate?.();
                    setErrorModal({ show: false, message: '' });
                  }}
                  style={{
                    backgroundColor: '#1976d2',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  {errorModal.navigationLabel}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile fixed top dropdown */}
      {isMobile && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 999,
            paddingTop: 40,
            background: '#ffffff',
            borderBottom: '1px solid #e5e7eb',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}
        >
          <div style={{ maxWidth: 1400, margin: '0 auto', padding: '12px 16px' }}>
            <div style={{ fontSize: 24, fontWeight: 300, color: '#374151', marginBottom: 8, textAlign: 'center',
              fontFamily: 'Raleway, sans-serif'
             }}>
              Settings
            </div>
            <select
              value={activeTab}
              onChange={(e) => setActiveTab((e.target as HTMLSelectElement).value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid #d1d5db',
                background: '#f9fafb',
                fontSize: 14,
                color: '#111827'
              }}
            >
              {isAdmin && <option value="accounts-billing">Accounts & Billing</option>}
              <option value="integrations">Integrations</option>
              {isAdmin && <option value="user-management">User Management</option>}
              <option value="market-data">Market Data</option>
              <option value="contact-support">Contact Support</option>
            </select>
          </div>
        </div>
      )}
      {/* Spacer to offset fixed header height on mobile */}
      {isMobile && <div style={{ height: 22 }} />}

      <div style={{ display: 'flex', gap: 24 }}>
        {/* Sidebar */}
        {!isMobile && (
        <div style={{ width: 250 }}>
          {/* <h3>Settings</h3> */}
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {isAdmin && (
              <li
                onClick={() => setActiveTab('accounts-billing')}
                style={{
                  padding: '12px 16px',
                  backgroundColor: activeTab === 'accounts-billing' ? '#f0f0f0' : 'transparent',
                  borderRadius: 8,
                  marginBottom: 8,
                  fontWeight: activeTab === 'accounts-billing' ? 'bold' : 'normal',
                  cursor: 'pointer'
                }}
              >
                Accounts & Billing
              </li>
            )}
            <li
              onClick={() => setActiveTab('integrations')}
              style={{
                padding: '12px 16px',
                backgroundColor: activeTab === 'integrations' ? '#f0f0f0' : 'transparent',
                borderRadius: 8,
                marginBottom: 8,
                fontWeight: activeTab === 'integrations' ? 'bold' : 'normal',
                cursor: 'pointer'
              }}
            >
              Integrations
            </li>
            {isAdmin && (
              <li
                onClick={() => setActiveTab('user-management')}
                style={{
                  padding: '12px 16px',
                  backgroundColor: activeTab === 'user-management' ? '#f0f0f0' : 'transparent',
                  borderRadius: 8,
                  marginBottom: 8,
                  fontWeight: activeTab === 'user-management' ? 'bold' : 'normal',
                  cursor: 'pointer'
                }}
              >
                User Management
              </li>
            )}

            <li
              onClick={() => setActiveTab('market-data')}
              style={{
                padding: '12px 16px',
                backgroundColor: activeTab === 'market-data' ? '#f0f0f0' : 'transparent',
                borderRadius: 8,
                marginBottom: 8,
                fontWeight: activeTab === 'market-data' ? 'bold' : 'normal',
                cursor: 'pointer'
              }}
            >
              Market Data
            </li>

            <li
              onClick={() => setActiveTab('contact-support')}
              style={{
                padding: '12px 16px',
                backgroundColor: activeTab === 'contact-support' ? '#f0f0f0' : 'transparent',
                borderRadius: 8,
                marginBottom: 8,
                fontWeight: activeTab === 'contact-support' ? 'bold' : 'normal',
                cursor: 'pointer'
              }}
            >
              Contact Support
            </li>
          </ul>
        </div>
        )}
        {/* Content */}
        <div style={{ 
          flex: 1,          
          maxWidth: `${isMobile ? '90%' : '100%'}`,          
          margin: '0 auto'
        }}>
          {activeTab === 'integrations' ? (
            isAdmin ? (
              configuring ? (
                // Show configuration form when an integration is being configured
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                    <h2 style={{ margin: 0 }}>{integrations.find(i => i.integration.Id === configuring)?.integration.Name} Configuration</h2>
                    <button
                      onClick={() => setCloseConfiguring()}
                      className="close-button"
                      style={{
                        background: '#f5f5f5',
                        border: '1px solid #ddd',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        fontSize: 18,
                        fontWeight: 'bold',
                        color: '#666',
                        width: 32,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        const target = e.target as HTMLButtonElement;
                        target.style.background = '#e0e0e0';
                        target.style.color = '#333';
                        target.style.borderColor = '#bbb';
                      }}
                      onMouseLeave={(e) => {
                        const target = e.target as HTMLButtonElement;
                        target.style.background = '#f5f5f5';
                        target.style.color = '#666';
                        target.style.borderColor = '#ddd';
                      }}
                    >
                      ×
                    </button>
                  </div>

                  {saveSuccess && (
                    <div style={{
                      backgroundColor: '#d4edda',
                      color: '#155724',
                      padding: '12px 16px',
                      borderRadius: 4,
                      marginBottom: 16
                    }}>
                      Configuration saved successfully!
                    </div>
                  )}

                  <div style={{
                    background: '#fff',
                    borderRadius: 8,
                    padding: 24,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                  }}>
                    {/* Only render GenericIntegrationConfig if we have a valid integration */}
                    {integrations.find(i => i.integration.Id === configuring) && (
                      <GenericIntegrationConfig
                        integration={integrations.find(i => i.integration.Id === configuring)!}
                        configValues={configValues}
                        configErrors={configErrors}
                        validating={validating}
                        validationSuccess={validationSuccess}
                        saving={saving}
                        onConfigChange={handleConfigChange}
                        onValidateConfig={handleValidateConfig}
                        onSaveConfig={handleSaveConfig}
                        customerIntegrations={integrations.find(i => i.integration.Id === configuring)?.customerIntegrations || []}
                        onAddIntegration={handleAddIntegration}
                        activeIntegrationIndex={activeIntegrationIndex}
                        setActiveIntegrationIndex={handleActiveIntegrationChange}
                      />
                    )}

                    {saveSuccess && (
                      <div style={{ marginTop: 16, color: 'green', textAlign: 'center' }}>
                        ✓ Configuration saved successfully!
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                // Show integrations list when no integration is being configured
                <div>
                  <ActivePlanDetails
                    planData={currentPlanObject}
                    loading={loading}
                    numberOfActiveIntegrations={integrations
                      .flatMap(i => i.customerIntegrations || [])
                      .filter(ci => ci.isEnabled)
                      .length}
                    enableCancellationFunctionality={false}
                    onNavigateToAccountsBilling={() => setActiveTab('accounts-billing')}
                    enableManagePlanNavigationButton={true}
                  />

                  <ActiveIntegrations
                    integrations={integrations}
                    loading={loading}
                    onRefresh={updateIntegrationStatuses}
                  />

                  <div style={{ marginTop: 40 }}>
                    <IntegrationsList
                      integrations={integrations}
                      onConfigure={handleConfigure}
                      onStatusToggle={handleStatusToggle}
                      onCustomerIntegrationToggle={handleCustomerIntegrationToggle}                      
                    />
                  </div>
                </div>
              )
            ) : (
              <div>
                <h3>Integrations</h3>
                <p>You need admin privileges to manage integrations.</p>
              </div>
            )
          ) : activeTab === 'user-management' ? (
            // User management tab
            <UserManagement />
          ) : activeTab === 'accounts-billing' ? (
            // Accounts & Billing tab
            <AccountsBilling onPlanUpdated={fetchIntegrations} />
          ) : activeTab === 'contact-support' ? (
            // Contact Support tab
            <div>
              <h3>Contact Support</h3>
              <div style={{
                background: '#fff',
                borderRadius: 8,
                padding: 24,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                maxWidth: 600
              }}>
                <p style={{ marginBottom: 20, color: '#666' }}>
                  Need help with execdash? Our support team is here to assist you.
                </p>
                <button
                  onClick={() => {
                    const customerName = currentUser?.customerName || 'Unknown';
                    const customerId = currentUser?.customerId || 'Unknown';
                    const subject = `Support Request - Customer: ${customerName} [${customerId}]`;
                    const mailtoUrl = `mailto:support@execdash.ai?subject=${encodeURIComponent(subject)}`;
                    window.location.href = mailtoUrl;
                  }}
                  style={{
                    background: 'linear-gradient(115deg, rgb(34, 34, 69) 25%, rgb(-77, 182, 159) 162%)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '500',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    (e.target as HTMLButtonElement).style.backgroundColor = '#1565c0';
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLButtonElement).style.backgroundColor = '#1976d2';
                  }}
                >
                  Send Support Email
                </button>
                <p style={{ marginTop: 16, fontSize: '14px', color: '#888' }}>
                  Clicking this button will open your default email program with a pre-filled support request.
                </p>
              </div>
            </div>
          ) : (
            // Market data tab
            <MarketDataSettings customerId={currentUser?.customerId || ''} />
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
